import bella from "@/assets/clients/bella.webp";
import devin from "@/assets/clients/devin.webp";
import distinkt from "@/assets/clients/distinkt.webp";
import omvPetrom from "@/assets/clients/omv-petrom.webp";
import siemens from "@/assets/clients/siemens.webp";
import sofiaAirport from "@/assets/clients/sofia-airport.webp";
import vivacom from "@/assets/clients/vivacom.webp";
import voBulgaria from "@/assets/clients/v-o-bulgaria.webp";
import acibadem from "@/assets/clients/acibadem-city-clinic.webp";
import novaBroadcasting from "@/assets/clients/nova-broadcasting-group.webp";
import enery from "@/assets/clients/enery.webp";
import a1 from "@/assets/clients/a1.webp";
import yettel from "@/assets/clients/yettel.webp";
import { useT } from "@/lib/i18n/I18nProvider";

const LOGOS: { name: string; src: string; width: number; height: number; hoverSrc?: string }[] = [
  { name: "Bella", src: bella, width: 609, height: 170 },
  { name: "Devin", src: devin, width: 367, height: 170 },
  { name: "Distinkt", src: distinkt, width: 582, height: 170 },
  { name: "OMV Petrom", src: omvPetrom, width: 1446, height: 170 },
  { name: "Siemens", src: siemens, width: 1071, height: 170 },
  { name: "Sofia Airport", src: sofiaAirport, width: 474, height: 170 },
  { name: "Vivacom", src: vivacom, width: 1723, height: 170 },
  { name: "V+O Bulgaria", src: voBulgaria, width: 370, height: 170 },
  { name: "Acibadem City Clinic", src: acibadem, width: 1732, height: 170 },
  { name: "Nova Broadcasting Group", src: novaBroadcasting, width: 170, height: 170 },
  { name: "Enery", src: enery, width: 522, height: 170 },
  { name: "A1", src: a1, width: 164, height: 170 },
  { name: "Yettel", src: yettel, width: 619, height: 170 },
];

function logoHeightPx(name: string): number {
  switch (name) {
    case "Siemens":
      return 25;
    case "Yettel":
      return 37;
    case "Vivacom":
      return 27;
    case "Distinkt":
      return 43;
    case "OMV Petrom":
      return 34;
    case "Bella":
      return 39;
    case "Acibadem City Clinic":
      return 33;
    case "A1":
    case "Sofia Airport":
      return 46;
    default:
      return 51;
  }
}

export function ClientLogoStrip() {
  const { locale } = useT();
  const label = locale === "bg" ? "Довериха ни се" : "Trusted by";
  const loop = [...LOGOS, ...LOGOS];
  return (
    <section className="relative overflow-x-hidden bg-primary py-14 text-primary-foreground lg:py-20">
      <div className="grid-overlay" />
      <div className="noise-overlay" />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 left-1/2 h-[360px] w-[360px] -translate-x-1/2 rounded-full opacity-20 blur-[140px]"
        style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
      />
      <div className="relative mx-auto max-w-[1200px] px-6 lg:px-10">
        <p className="text-center text-[11px] font-semibold uppercase tracking-[0.32em] text-white/80">
          {label}
        </p>
        <div
          className="group relative mt-8 -my-10 py-10 [overflow-x:clip] [overflow-y:visible]"
          style={{
            maskImage:
              "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
          }}
        >
          <ul className="marquee-track flex w-max items-center gap-[66px] group-hover:[animation-play-state:paused]">
            {loop.map((logo, i) => {
              const h = logoHeightPx(logo.name);
              // Scale glow proportionally to logo height (baseline 60px).
              const scale = h / 60;
              const spread = Math.round(28 * scale); // px of negative margin / outward bleed
              const blur = Math.round(56 * scale); // px of gaussian blur
              return (
                <li
                  key={`${logo.name}-${i}`}
                  className="group/logo relative flex shrink-0 items-center justify-center px-3"
                  aria-hidden={i >= LOGOS.length ? true : undefined}
                  tabIndex={i >= LOGOS.length ? -1 : 0}
                >
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-[32px] opacity-0 scale-90 transition-all duration-500 ease-out group-hover/logo:opacity-100 group-hover/logo:scale-110 group-focus-visible/logo:opacity-100 group-focus-visible/logo:scale-110"
                    style={{
                      margin: `-${spread}px`,
                      filter: `blur(${blur}px)`,
                      background:
                        "radial-gradient(circle at center, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.25) 45%, transparent 75%)",
                    }}
                  />
                  <img
                    src={logo.src}
                    alt={i >= LOGOS.length ? "" : `${logo.name} logo`}
                    loading="lazy"
                    width={logo.width}
                    height={logo.height}
                    style={{ height: `${h}px`, width: "auto" }}
                    className={`relative object-contain opacity-70 transition duration-300 ease-out [filter:brightness(0)_invert(1)_drop-shadow(0_0_0.4px_rgba(255,255,255,0.6))] ${
                      logo.hoverSrc
                        ? "group-hover/logo:opacity-0 group-focus-visible/logo:opacity-0"
                        : "group-hover/logo:opacity-100 group-hover/logo:[filter:drop-shadow(0_0_0.5px_rgba(0,0,0,0.35))_drop-shadow(0_0_0.5px_rgba(0,0,0,0.2))_drop-shadow(0_0_18px_rgba(255,255,255,0.55))] group-focus-visible/logo:opacity-100 group-focus-visible/logo:[filter:drop-shadow(0_0_0.5px_rgba(0,0,0,0.35))_drop-shadow(0_0_0.5px_rgba(0,0,0,0.2))_drop-shadow(0_0_18px_rgba(255,255,255,0.55))]"
                    }`}
                  />
                  {logo.hoverSrc && (
                    <img
                      src={logo.hoverSrc}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      width={logo.width}
                      height={logo.height}
                      style={{ height: `${h}px`, width: "auto" }}
                      className="pointer-events-none absolute object-contain opacity-0 transition duration-300 ease-out [filter:drop-shadow(0_0_0.5px_rgba(0,0,0,0.35))_drop-shadow(0_0_0.5px_rgba(0,0,0,0.2))_drop-shadow(0_0_18px_rgba(255,255,255,0.55))] group-hover/logo:opacity-100 group-focus-visible/logo:opacity-100"
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}